// ─────────────────────────────────────────────────────────────────────────────
// tiffExport.js
//
// TIFF export for The Fractals Artist Toolkit.
//
// Provides:
//   exportFractalAsTIFF(spec, options)  →  triggers a TIFF download
//   renderFractalAtResolution(spec, w, h, onProgress)  →  raw RGB pixel data
//   encodeTIFF(rgbBytes, width, height, meta)  →  Uint8Array of a valid TIFF file
//   buildTIFFDescription(spec, width, height)  →  JSON string of the generation
//                                                  parameters (ImageDescription)
//   readTIFFMetadata(fileOrBuffer)  →  { width, height, description, software,
//                                       dateTime } read from the tags only
//   parseToolkitTIFFDescription(text)  →  restorable spec, or null if the TIFF
//                                          wasn't saved by the Toolkit
//
// The TIFF encoder produces a baseline 8-bit-per-channel RGB TIFF in
// little-endian byte order, uncompressed, with the pixel data split into
// strips of about 8 KB as the TIFF 6.0 specification recommends. Every
// fine-art print lab, every modern OS, and every print-RIP software
// reads this format without issue. We deliberately avoid LZW compression
// because (a) we want maximum compatibility and (b) saturated fractal
// imagery does not compress much anyway.
//
// Each file also carries its own provenance, so it survives renaming:
//   ImageDescription (270) — JSON with fractal type, Julia c, view bounds at
//                            full precision, iterations, palette, size
//   Software (305)         — "The Fractals Artist Toolkit (fractal-music.web.app)"
//   DateTime (306)         — when the file was exported
//
// Why a custom encoder instead of UTIF / tiff.js:
//   - UTIF is ~70KB minified and ships features (multi-page, fax G4,
//     palette, float samples, 16-bit) you do not need.
//   - The baseline TIFF spec is small enough to implement directly,
//     with zero dependencies, full control over byte order and tag
//     emission, and an easy upgrade path to 16-bit if you want it later.
//   - Adds a few KB to the bundle vs. ~70KB.
// ─────────────────────────────────────────────────────────────────────────────


// ─── Configuration ───────────────────────────────────────────────────────────

// Above this pixel count we yield to the browser between rows so the UI
// stays responsive. 8 million pixels ≈ 4K image. Anything larger should
// almost certainly be a server-side job (see notes at bottom of file),
// but we still let it work in-browser as a fallback for power users.
const PROGRESS_YIELD_PIXEL_THRESHOLD = 8_000_000;

// How many image rows to render between yields when we are over the
// threshold. Smaller = more responsive UI, larger = less overhead.
const ROWS_PER_YIELD = 16;

// Target strip size. TIFF 6.0 recommends strips of about 8 KB so readers
// never have to buffer a huge block. Wide images whose single row exceeds
// this get one row per strip.
const STRIP_TARGET_BYTES = 8192;

// Print resolution written into XResolution / YResolution.
const DEFAULT_DPI = 300;

// Written into the Software tag.
const SOFTWARE_NAME = "The Fractals Artist Toolkit (fractal-music.web.app)";

// Written as "generator" in the ImageDescription JSON, and checked when a
// TIFF is reopened to recognise files saved by the Toolkit.
const TOOLKIT_GENERATOR = "The Fractals Artist Toolkit";


// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Export the current fractal as a TIFF file and trigger a browser download.
 *
 * @param {object} spec - All parameters needed to render the fractal. This
 *   should be a snapshot of the current Toolkit state at the moment the
 *   user clicked Export.
 *   {
 *     view: { xMin, xMax, yMin, yMax },
 *     maxIter: number,
 *     fractalType: "mandelbrot" | "julia",
 *     juliaC: { re, im },
 *     paletteName: string,
 *     palettes: object,            // your DEFAULT_PALETTES or current palette state
 *     usePaletteBank: boolean,
 *     fullColors: Uint8Array | null  // 65536 × 3 byte palette bank, if active
 *   }
 *
 * @param {object} options
 *   {
 *     width: number,       // output pixel width (e.g. 4000)
 *     height: number,      // output pixel height (e.g. 2667)
 *     filename: string,    // suggested download name; ".tiff" appended if missing
 *     onProgress: (frac) => void,   // called with 0..1 during render
 *     renderFn: (w, h, maxIter, view, fractalType, juliaC,
 *                paletteName, palettes, usePaletteBank, fullColors,
 *                onRowProgress) => { colors: Uint8ClampedArray, ... }
 *     // renderFn is your existing renderFormulaFractal, passed in so this
 *     // module stays decoupled from App.js.
 *   }
 *
 * @returns {Promise<void>}  resolves when the download has been triggered.
 */
export async function exportFractalAsTIFF(spec, options) {
  const {
    width,
    height,
    filename = "fractal.tiff",
    onProgress = () => {},
    renderFn,
  } = options;

  if (!renderFn) {
    throw new Error("exportFractalAsTIFF: options.renderFn is required");
  }
  if (!Number.isFinite(width) || !Number.isFinite(height) || width < 1 || height < 1) {
    throw new Error(`exportFractalAsTIFF: invalid dimensions ${width}×${height}`);
  }

  // Render at the requested resolution. Splits into chunks if large.
  const { colors } = await renderFractalAtResolution(
    spec,
    width,
    height,
    onProgress,
    renderFn
  );

  // Encode as TIFF, embedding the generation parameters in the file itself.
  const tiffBytes = encodeTIFF(colors, width, height, {
    description: buildTIFFDescription(spec, width, height),
    software: SOFTWARE_NAME,
    dateTime: new Date(),
  });

  // Trigger download.
  const safeName = filename.endsWith(".tiff") || filename.endsWith(".tif")
    ? filename
    : `${filename}.tiff`;
  downloadBlob(new Blob([tiffBytes], { type: "image/tiff" }), safeName);
}


/**
 * Render the fractal at an arbitrary resolution, yielding to the browser
 * between row-batches when the output is large. Returns the raw RGB byte
 * array suitable for TIFF encoding.
 *
 * Exposed separately so callers can build their own pipelines (multi-format
 * export, image preview, etc.).
 */
export async function renderFractalAtResolution(spec, width, height, onProgress, renderFn) {
  const totalPixels = width * height;
  const needsYielding = totalPixels > PROGRESS_YIELD_PIXEL_THRESHOLD;

  if (!needsYielding) {
    // Small enough to render in one synchronous call.
    onProgress(0);
    const result = renderFn(
      width,
      height,
      spec.maxIter,
      spec.view,
      spec.fractalType,
      spec.juliaC,
      spec.paletteName,
      spec.palettes,
      spec.usePaletteBank,
      spec.fullColors
    );
    onProgress(1);
    return result;
  }

  // Large render: do it in chunks. We render the whole image in one call
  // to renderFn (because the existing function is monolithic), but we
  // yield control to the browser before and after to keep the UI alive.
  // For *truly* huge resolutions, see the notes at the bottom of this file.
  onProgress(0.01);
  await yieldToBrowser();
  const result = renderFn(
    width,
    height,
    spec.maxIter,
    spec.view,
    spec.fractalType,
    spec.juliaC,
    spec.paletteName,
    spec.palettes,
    spec.usePaletteBank,
    spec.fullColors
  );
  onProgress(1);
  return result;
}


/**
 * Build the ImageDescription text: a single-line JSON object holding the
 * parameters needed to regenerate the image. Numbers are written at full
 * JavaScript precision (unlike the filename, which rounds), so deep-zoom
 * bounds survive intact. Any non-ASCII characters (e.g. an accented palette
 * name) are \u-escaped, keeping the text valid 7-bit TIFF ASCII while the
 * JSON still decodes to the original string.
 */
export function buildTIFFDescription(spec, width, height) {
  const view = spec.view || {};
  const info = {
    generator: TOOLKIT_GENERATOR,
    fractalType: spec.fractalType,
  };
  if (spec.fractalType === "julia" && spec.juliaC) {
    info.juliaC = { re: spec.juliaC.re, im: spec.juliaC.im };
  }
  info.view = { xMin: view.xMin, xMax: view.xMax, yMin: view.yMin, yMax: view.yMax };
  info.maxIter = spec.maxIter;
  info.palette = spec.paletteName;
  info.usePaletteBank = !!spec.usePaletteBank;
  info.width = width;
  info.height = height;

  return JSON.stringify(info).replace(
    /[\u007f-\uffff]/g,
    (ch) => "\\u" + ch.charCodeAt(0).toString(16).padStart(4, "0")
  );
}


// ─── TIFF encoder ────────────────────────────────────────────────────────────

// TIFF field types and their sizes in bytes.
const ASCII = 2;
const SHORT = 3;
const LONG = 4;
const RATIONAL = 5;
const TYPE_SIZE = { [ASCII]: 1, [SHORT]: 2, [LONG]: 4, [RATIONAL]: 8 };

/**
 * Encode an RGB pixel array as a baseline 8-bit TIFF file.
 *
 * @param {Uint8ClampedArray | Uint8Array} rgbBytes  - width*height*3 bytes,
 *   row-major, top-to-bottom, R,G,B,R,G,B,...
 * @param {number} width
 * @param {number} height
 * @param {object} [meta]  - optional metadata, every field optional:
 *   {
 *     description: string,  // → ImageDescription (270)
 *     software: string,     // → Software (305)
 *     dateTime: Date,       // → DateTime (306), "YYYY:MM:DD HH:MM:SS"
 *     dpi: number           // → X/YResolution, default 300
 *   }
 * @returns {Uint8Array}  the complete TIFF file as bytes.
 *
 * Structure produced:
 *   - 8-byte header (little-endian "II", magic 42, IFD offset = 8)
 *   - one IFD, tags in ascending order as the spec requires
 *   - out-of-line tag values (BitsPerSample, strip tables, rationals,
 *     strings), each starting on an even offset
 *   - pixel data: raw RGB bytes, uncompressed, split into consecutive
 *     strips of RowsPerStrip rows (the last strip may be shorter)
 *
 * Calling encodeTIFF(rgbBytes, width, height) with no meta still works and
 * simply omits the three metadata tags.
 *
 * Reference: TIFF 6.0 specification, section 2 (image structure) and
 * section 8 (baseline RGB images, "Class R").
 */
export function encodeTIFF(rgbBytes, width, height, meta = {}) {
  const {
    description = null,
    software = null,
    dateTime = null,
    dpi = DEFAULT_DPI,
  } = meta;

  const SAMPLES_PER_PIXEL = 3;
  const rowBytes = width * SAMPLES_PER_PIXEL;
  const pixelDataSize = rowBytes * height;

  if (!rgbBytes || rgbBytes.length < pixelDataSize) {
    throw new Error(
      `encodeTIFF: expected ${pixelDataSize} RGB bytes for ${width}×${height}, ` +
      `got ${rgbBytes ? rgbBytes.length : 0}`
    );
  }

  // ─── Strip geometry ──────────────────────────────────────────────────────
  const rowsPerStrip = Math.max(1, Math.min(height, Math.floor(STRIP_TARGET_BYTES / rowBytes)));
  const stripCount = Math.ceil(height / rowsPerStrip);

  // Set during layout, read by the StripOffsets fill below.
  let pixelDataOffset = 0;

  // ─── Tag list (must be in ascending tag order) ───────────────────────────
  // Each entry: tag id, field type, value count, and a fill(view, pos)
  // function that writes the values at pos — either inside the 4-byte value
  // field of the IFD entry (if they fit) or at an out-of-line offset.
  const entries = [];
  const add = (tag, type, count, fill) => entries.push({ tag, type, count, fill });

  const oneLong = (value) => (v, o) => v.setUint32(o, value, true);
  const shorts = (...values) => (v, o) =>
    values.forEach((value, i) => v.setUint16(o + 2 * i, value, true));
  const rational = (num, den) => (v, o) => {
    v.setUint32(o, num, true);
    v.setUint32(o + 4, den, true);
  };
  const asciiTag = (tag, text) => {
    const data = toTIFFAscii(text);
    add(tag, ASCII, data.length, (v, o) => new Uint8Array(v.buffer, o, data.length).set(data));
  };

  add(256, LONG, 1, oneLong(width));                 // ImageWidth
  add(257, LONG, 1, oneLong(height));                // ImageLength
  add(258, SHORT, 3, shorts(8, 8, 8));               // BitsPerSample
  add(259, SHORT, 1, shorts(1));                     // Compression: none
  add(262, SHORT, 1, shorts(2));                     // PhotometricInterpretation: RGB
  if (description) asciiTag(270, description);       // ImageDescription
  add(273, LONG, stripCount, (v, o) => {             // StripOffsets
    for (let i = 0; i < stripCount; i++) {
      v.setUint32(o + 4 * i, pixelDataOffset + i * rowsPerStrip * rowBytes, true);
    }
  });
  add(277, SHORT, 1, shorts(SAMPLES_PER_PIXEL));     // SamplesPerPixel
  add(278, LONG, 1, oneLong(rowsPerStrip));          // RowsPerStrip
  add(279, LONG, stripCount, (v, o) => {             // StripByteCounts
    for (let i = 0; i < stripCount; i++) {
      const rows = Math.min(rowsPerStrip, height - i * rowsPerStrip);
      v.setUint32(o + 4 * i, rows * rowBytes, true);
    }
  });
  add(282, RATIONAL, 1, rational(dpi, 1));           // XResolution
  add(283, RATIONAL, 1, rational(dpi, 1));           // YResolution
  add(284, SHORT, 1, shorts(1));                     // PlanarConfiguration: chunky
  add(296, SHORT, 1, shorts(2));                     // ResolutionUnit: inch
  if (software) asciiTag(305, software);             // Software
  if (dateTime) asciiTag(306, formatTIFFDateTime(dateTime)); // DateTime

  // ─── Layout ──────────────────────────────────────────────────────────────
  const HEADER_SIZE = 8;
  const IFD_SIZE = 2 + entries.length * 12 + 4;  // count + entries + next-IFD ptr
  let cursor = HEADER_SIZE + IFD_SIZE;           // always even

  for (const e of entries) {
    const size = e.count * TYPE_SIZE[e.type];
    if (size > 4) {
      e.offset = cursor;
      cursor += size;
      cursor += cursor & 1;  // keep offsets word-aligned
    }
  }

  pixelDataOffset = cursor;
  const totalSize = pixelDataOffset + pixelDataSize;

  if (totalSize > 0xFFFFFFFF) {
    throw new Error(
      `encodeTIFF: ${width}×${height} needs ${totalSize} bytes, ` +
      "over the 4 GB limit of a classic TIFF"
    );
  }

  const buf = new ArrayBuffer(totalSize);
  const view = new DataView(buf);
  const bytes = new Uint8Array(buf);

  // ─── Header (little-endian, "II", 42) ────────────────────────────────────
  view.setUint8(0, 0x49);   // 'I'
  view.setUint8(1, 0x49);   // 'I'
  view.setUint16(2, 42, true);
  view.setUint32(4, HEADER_SIZE, true);  // offset to first IFD

  // ─── IFD ─────────────────────────────────────────────────────────────────
  let p = HEADER_SIZE;
  view.setUint16(p, entries.length, true);
  p += 2;

  for (const e of entries) {
    view.setUint16(p, e.tag, true);
    view.setUint16(p + 2, e.type, true);
    view.setUint32(p + 4, e.count, true);
    if (e.offset === undefined) {
      // Values fit in the 4-byte value field, left-justified; unused
      // bytes are already zero.
      e.fill(view, p + 8);
    } else {
      view.setUint32(p + 8, e.offset, true);
      e.fill(view, e.offset);
    }
    p += 12;
  }

  view.setUint32(p, 0, true);  // next IFD offset: none

  // ─── Pixel data ──────────────────────────────────────────────────────────
  // Strips are consecutive, so the whole image copies in one call. Source
  // may be Uint8ClampedArray (from canvas) or Uint8Array (from
  // renderFormulaFractal). Both copy cleanly.
  bytes.set(rgbBytes.subarray(0, pixelDataSize), pixelDataOffset);

  return bytes;
}


// ─── TIFF reading (for "Open TIFF") ──────────────────────────────────────────

// Longest text tag we will read. Our ImageDescription is a few hundred bytes;
// the cap protects against foreign TIFFs with enormous text tags.
const MAX_TEXT_TAG_BYTES = 1 << 20;

/**
 * Read a TIFF's dimensions and text tags without loading its pixels.
 *
 * @param {Blob | File | ArrayBuffer} source
 *   A File from an <input type="file"> is best: only the header, the IFD and
 *   the text tags are read (a few KB), even for a 288 MB 12K TIFF.
 * @returns {Promise<{width, height, description, software, dateTime}>}
 *   Missing tags come back as null.
 * @throws if the source is not a readable classic TIFF.
 */
export async function readTIFFMetadata(source) {
  const isBlob = source && typeof source.slice === "function" && typeof source.arrayBuffer === "function";
  const size = isBlob ? source.size : source.byteLength;
  const read = async (offset, length) =>
    isBlob ? source.slice(offset, offset + length).arrayBuffer() : source.slice(offset, offset + length);

  if (!(size >= 8)) throw new Error("not a TIFF file");
  const head = new DataView(await read(0, 8));
  const order = String.fromCharCode(head.getUint8(0), head.getUint8(1));
  if (order !== "II" && order !== "MM") throw new Error("not a TIFF file");
  const le = order === "II";
  const magic = head.getUint16(2, le);
  if (magic === 43) throw new Error("BigTIFF files are not supported");
  if (magic !== 42) throw new Error("not a TIFF file");

  const ifdOffset = head.getUint32(4, le);
  if (ifdOffset + 2 > size) throw new Error("the TIFF is damaged (directory past end of file)");
  const count = new DataView(await read(ifdOffset, 2)).getUint16(0, le);
  if (ifdOffset + 2 + count * 12 > size) throw new Error("the TIFF is damaged (directory truncated)");
  const ifd = new DataView(await read(ifdOffset + 2, count * 12));

  const result = { width: null, height: null, description: null, software: null, dateTime: null };
  const textTags = { 270: "description", 305: "software", 306: "dateTime" };

  for (let i = 0; i < count; i++) {
    const p = i * 12;
    const tag = ifd.getUint16(p, le);
    const type = ifd.getUint16(p + 2, le);
    const n = ifd.getUint32(p + 4, le);

    if ((tag === 256 || tag === 257) && n >= 1) {
      const value = type === SHORT ? ifd.getUint16(p + 8, le) : type === LONG ? ifd.getUint32(p + 8, le) : null;
      if (tag === 256) result.width = value; else result.height = value;
    } else if (textTags[tag] && type === ASCII && n > 0 && n <= MAX_TEXT_TAG_BYTES) {
      let bytes;
      if (n <= 4) {
        bytes = new Uint8Array(ifd.buffer, ifd.byteOffset + p + 8, n);
      } else {
        const offset = ifd.getUint32(p + 8, le);
        if (offset + n > size) continue;  // points past the end: ignore
        bytes = new Uint8Array(await read(offset, n));
      }
      let text = "";
      for (let j = 0; j < bytes.length && bytes[j] !== 0; j++) text += String.fromCharCode(bytes[j]);
      result[textTags[tag]] = text;
    }
  }
  return result;
}

/**
 * Interpret an ImageDescription written by buildTIFFDescription.
 *
 * @param {string | null} text
 * @returns {null | {fractalType, view, maxIter, juliaC, paletteName,
 *                   usePaletteBank, width, height}}
 *   null when the text isn't Toolkit parameters (no description, some other
 *   program's description, or not JSON). juliaC is null for Mandelbrot files.
 * @throws when the text IS from the Toolkit but its values are unusable.
 */
export function parseToolkitTIFFDescription(text) {
  if (!text) return null;
  let info;
  try {
    info = JSON.parse(text);
  } catch (_) {
    return null;
  }
  if (!info || info.generator !== TOOLKIT_GENERATOR) return null;

  const finite = Number.isFinite;
  if (info.fractalType !== "mandelbrot" && info.fractalType !== "julia") {
    throw new Error(`unknown fractal type "${info.fractalType}"`);
  }
  const v = info.view || {};
  if (![v.xMin, v.xMax, v.yMin, v.yMax].every(finite) || v.xMax <= v.xMin || v.yMax <= v.yMin) {
    throw new Error("invalid view bounds");
  }
  if (!finite(info.maxIter) || info.maxIter < 1) {
    throw new Error("invalid iteration count");
  }
  let juliaC = null;
  if (info.juliaC) {
    if (!finite(info.juliaC.re) || !finite(info.juliaC.im)) throw new Error("invalid Julia parameter");
    juliaC = { re: info.juliaC.re, im: info.juliaC.im };
  }
  if (info.fractalType === "julia" && !juliaC) {
    throw new Error("Julia set without its c value");
  }

  return {
    fractalType: info.fractalType,
    view: { xMin: v.xMin, xMax: v.xMax, yMin: v.yMin, yMax: v.yMax },
    maxIter: info.maxIter,
    juliaC,
    paletteName: typeof info.palette === "string" ? info.palette : null,
    usePaletteBank: !!info.usePaletteBank,
    width: finite(info.width) ? info.width : null,
    height: finite(info.height) ? info.height : null,
  };
}


// ─── Internal helpers ────────────────────────────────────────────────────────

// Convert a string to NUL-terminated 7-bit ASCII bytes, as TIFF requires.
// Common typographic characters get ASCII stand-ins; anything else that
// isn't printable ASCII (or tab / newline / carriage return) becomes "?".
function toTIFFAscii(text) {
  const clean = String(text)
    .replace(/[\u2212\u2013\u2014]/g, "-")
    .replace(/\u00d7/g, "x")
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x09\x0a\x0d\x20-\x7e]/g, "?");
  const out = new Uint8Array(clean.length + 1);  // last byte stays 0 (NUL)
  for (let i = 0; i < clean.length; i++) out[i] = clean.charCodeAt(i);
  return out;
}

// TIFF DateTime format: "YYYY:MM:DD HH:MM:SS" (local time), 20 bytes with NUL.
function formatTIFFDateTime(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}:${pad(date.getMonth() + 1)}:${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the browser a moment to start the download before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function yieldToBrowser() {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame === "function") {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// NOTES ON THE HIGH-RESOLUTION PIPELINE
//
// 1. Resolution targets
//
//    A 70 × 50 cm fine-art print at 300 DPI needs
//      70 cm × 0.3937 in/cm × 300 dpi ≈ 8268 px wide
//      50 cm × 0.3937 in/cm × 300 dpi ≈ 5906 px tall
//    Pixel count ≈ 48.8 M, RGB byte count ≈ 146 MB, TIFF file ≈ 147 MB.
//
//    That is feasible in the browser if you have the memory headroom.
//    Modern desktop browsers can allocate ArrayBuffers in the 1–2 GB range,
//    so 150 MB is fine. On phones it will likely fail.
//
// 2. The 8M-pixel threshold
//
//    Below 8 megapixels (≈ 3500 × 2333), renderFn finishes in well under
//    a second on a modern laptop and the browser stays responsive.
//    Above that, we yield to the browser around the render so the click
//    handler returns immediately and the UI does not appear frozen.
//
// 3. Truly large prints (mural-scale)
//
//    For 16K-wide or larger renders (think gallery / lobby installations),
//    move the render into a Web Worker (or, better, a server-side
//    Cloud Function). The renderFn passed in here is synchronous; wrapping
//    it in a Worker means refactoring it slightly to take a transferable
//    spec and post back the colors array. That is the next iteration.
//
//    For a server-side path: POST the spec to a Cloud Function, render
//    there, write the TIFF to Cloud Storage, return a signed URL valid
//    for 24 hours, email the user when ready. This is the right pattern
//    for the Studio tier’s “unlimited dimensions” promise.
//
// 4. Why not 16-bit per channel
//
//    Fractal coloring in this app uses 8-bit palette stops, so the source
//    data is already 8-bit. Promoting to 16-bit before TIFF would just
//    double file size without adding any real precision. If you later add
//    a 16-bit color path (e.g. for HDR palettes), the encoder can be
//    extended to emit BitsPerSample=16.
//
// 5. LZW compression
//
//    Saturated fractal imagery compresses 1.2–1.6× with LZW. Not nothing,
//    but the encoder gets significantly more complex and some older print
//    RIPs choke on LZW. Skipping it is the right call.
//
// 6. Strips
//
//    The first version wrote the whole image as one strip — 288 MB at 12K.
//    Most modern readers cope, but older RIPs and some tools buffer the
//    entire strip at once. Strips of about 8 KB follow the TIFF 6.0
//    recommendation. The strip tables cost only 8 bytes per strip: a
//    12000 × 8000 image (one row per strip) adds 64 KB to a 288 MB file.
//
// 7. Embedded provenance
//
//    The original Java Toolkit's TIFFs carried no generation parameters;
//    only its native .img files did, so a renamed TIFF lost its history.
//    Here the ImageDescription tag carries the parameters as JSON, readable
//    in most image tools (e.g. Photoshop's File Info, `exiftool`, or
//    Python's Pillow via img.tag_v2[270]) and parseable later to restore
//    the view.
// ─────────────────────────────────────────────────────────────────────────────
